import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ArrowRight,
    BadgeCheck,
    Heart,
    HeartHandshake,
    Lightbulb,
    MapPin,
    ShieldCheck,
    SmilePlus,
    Sparkles,
    Target,
    Users,
    Eye,
} from 'lucide-react';

import PublicLayout from '@/components/public/PublicLayout';
import { ABOUT_CONTENT, COMPANY, ROUTES } from '@/lib/data';
import { usePublicContent } from '@/lib/usePublicContent';
import { fadeUp, stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';

const VALUE_ICONS = {
    ShieldCheck,
    SmilePlus,
    Lightbulb,
    Users,
    BadgeCheck,
    Heart,
    HeartHandshake,
};

const VALUE_STYLES = [
    'bg-[hsl(var(--kche-pink))]/15 text-[hsl(var(--kche-pink))] group-hover:bg-[hsl(var(--kche-pink))] group-hover:text-white',
    'bg-[hsl(var(--kche-yellow))]/25 text-[hsl(var(--kche-purple))] group-hover:bg-[hsl(var(--kche-yellow))] group-hover:text-[hsl(var(--kche-dark))]',
    'bg-[hsl(var(--kche-cyan))]/15 text-[hsl(var(--kche-blue))] group-hover:bg-[hsl(var(--kche-cyan))] group-hover:text-[hsl(var(--kche-dark))]',
    'bg-[hsl(var(--kche-green))]/15 text-[hsl(var(--kche-green))] group-hover:bg-[hsl(var(--kche-green))] group-hover:text-white',
    'bg-[hsl(var(--kche-purple))]/15 text-[hsl(var(--kche-purple))] group-hover:bg-[hsl(var(--kche-purple))] group-hover:text-white',
];

export default function NosotrosPage() {
    const { content, settings } = usePublicContent();

    const title = content.about_title || ABOUT_CONTENT.title;
    const intro = content.about_body || ABOUT_CONTENT.intro;
    const mission = content.about_mission || ABOUT_CONTENT.mission;
    const vision = content.about_vision || ABOUT_CONTENT.vision;
    const address = settings.address || COMPANY.address;

    return (
        <PublicLayout>
            <section className="relative overflow-hidden bg-gradient-sky text-white py-16 sm:py-20 lg:py-24">
                <div className="noise-overlay" />
                <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[hsl(var(--kche-yellow))]/35 blur-3xl animate-blob" />
                <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-[hsl(var(--kche-pink))]/35 blur-3xl animate-blob" />

                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center relative">
                    <motion.div initial="hidden" animate="show" variants={stagger}>
                        <motion.p
                            variants={fadeUp}
                            className="inline-flex items-center gap-2 text-sm font-bold font-display text-[hsl(var(--kche-yellow))] uppercase tracking-wider bg-white/15 backdrop-blur rounded-full px-4 py-2 border border-white/25"
                        >
                            <Sparkles className="size-4" />
                            Nosotros
                        </motion.p>

                        <motion.h1
                            variants={fadeUp}
                            className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-display font-bold leading-tight"
                        >
                            {title}
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="mt-5 text-white/95 leading-relaxed text-lg sm:text-xl max-w-2xl"
                        >
                            {intro}
                        </motion.p>

                        <motion.div
                            variants={fadeUp}
                            className="mt-6 inline-flex items-center gap-2 text-sm bg-white/15 backdrop-blur rounded-full px-4 py-2 border border-white/30"
                        >
                            <MapPin className="size-4 text-[hsl(var(--kche-yellow))]" />
                            {address}
                        </motion.div>

                        <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to={ROUTES.atracciones}
                                className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--kche-yellow))] text-[hsl(var(--kche-dark))] px-6 py-3 font-bold font-display shadow-pop hover:scale-[1.03] transition-transform"
                            >
                                Ver atracciones
                                <ArrowRight className="size-5" />
                            </Link>

                            <Link
                                to={ROUTES.contacto}
                                className="inline-flex items-center gap-2 rounded-full bg-white/15 text-white px-6 py-3 font-bold font-display border border-white/30 backdrop-blur hover:bg-white hover:text-[hsl(var(--kche-blue))] transition-colors"
                            >
                                Contacto
                            </Link>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.55 }}
                        className="relative"
                    >
                        <div className="absolute -inset-4 rounded-[2rem] bg-white/15 blur-2xl" />

                        <div className="relative rounded-[2rem] bg-white/15 backdrop-blur border border-white/30 p-5 sm:p-6 shadow-pop">
                            <div className="rounded-3xl bg-white text-foreground p-6 sm:p-7 hover:scale-[1.01] transition-transform">
                                <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-[hsl(var(--kche-yellow))] text-[hsl(var(--kche-dark))] shadow-soft">
                                    <Target className="size-7" />
                                </span>
                                <h2 className="mt-5 text-3xl font-display font-bold">
                                    Diversión con propósito
                                </h2>
                                <p className="mt-3 text-muted-foreground leading-relaxed">
                                    Cada experiencia busca unir a la familia, crear recuerdos y ofrecer un espacio de entretenimiento para todas las generaciones.
                                </p>
                            </div>

                            <div className="mt-4 grid grid-cols-3 gap-3">
                                {['Seguridad', 'Innovación', 'Familia'].map((item, index) => (
                                    <div
                                        key={item}
                                        className="rounded-2xl bg-white/20 border border-white/25 px-3 py-4 text-center hover:bg-white hover:text-[hsl(var(--kche-blue))] transition-colors"
                                    >
                                        <p className="text-xs sm:text-sm font-display font-bold">
                                            {item}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <section className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24">
                <div className="absolute top-10 right-0 w-72 h-72 rounded-full bg-[hsl(var(--kche-cyan))]/15 blur-3xl" />
                <div className="absolute bottom-10 left-0 w-72 h-72 rounded-full bg-[hsl(var(--kche-pink))]/10 blur-3xl" />

                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
                    <div className="text-center max-w-3xl mx-auto">
                        <p className="text-sm font-bold font-display text-[hsl(var(--kche-pink))] uppercase tracking-wider">
                            Lo que nos mueve
                        </p>
                        <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-bold">
                            Misión y <span className="text-gradient-fun">visión</span>
                        </h2>
                        <p className="mt-3 text-muted-foreground text-base sm:text-lg">
                            La esencia de K’che está en crear momentos seguros, emocionantes y memorables para toda la familia.
                        </p>
                    </div>

                    <motion.div
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, amount: 0.18 }}
                        variants={stagger}
                        className="mt-10 grid gap-6 lg:grid-cols-2"
                    >
                        <motion.article
                            variants={fadeUp}
                            className="group relative overflow-hidden rounded-[2rem] bg-gradient-fun text-white p-7 sm:p-8 shadow-pop hover:-translate-y-1 transition-transform"
                        >
                            <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-white/20 blur-3xl" />
                            <span className="relative inline-flex size-14 items-center justify-center rounded-2xl bg-white/20 backdrop-blur border border-white/25">
                                <Target className="size-7" />
                            </span>
                            <h3 className="relative mt-5 text-3xl font-display font-bold">
                                Misión
                            </h3>
                            <p className="relative mt-3 leading-relaxed text-white/95">
                                {mission}
                            </p>
                        </motion.article>

                        <motion.article
                            variants={fadeUp}
                            className="group relative overflow-hidden rounded-[2rem] bg-gradient-sunshine text-[hsl(var(--kche-dark))] p-7 sm:p-8 shadow-pop hover:-translate-y-1 transition-transform"
                        >
                            <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-white/35 blur-3xl" />
                            <span className="relative inline-flex size-14 items-center justify-center rounded-2xl bg-white/35 backdrop-blur border border-white/30">
                                <Eye className="size-7" />
                            </span>
                            <h3 className="relative mt-5 text-3xl font-display font-bold">
                                Visión
                            </h3>
                            <p className="relative mt-3 leading-relaxed text-[hsl(var(--kche-dark))]/85">
                                {vision}
                            </p>
                        </motion.article>
                    </motion.div>
                </div>
            </section>

            <section className="relative overflow-hidden bg-kche-soft py-16 sm:py-20 lg:py-24">
                <div className="absolute inset-x-0 top-0 h-3 ribbon-diagonal" />

                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto">
                        <p className="text-sm font-bold font-display text-[hsl(var(--kche-blue))] uppercase tracking-wider">
                            Valores
                        </p>
                        <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-bold">
                            Nuestra forma de <span className="text-gradient-sky">crear diversión</span>
                        </h2>
                        <p className="mt-3 text-muted-foreground text-base sm:text-lg">
                            Estos principios guían la experiencia que queremos entregar a cada familia que nos visita.
                        </p>
                    </div>

                    <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.15 }}
    variants={stagger}
    className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-12"
>
    {ABOUT_CONTENT.values.map((value, index) => {
        const Icon = VALUE_ICONS[value.icon] || Sparkles;
        const color = VALUE_STYLES[index % VALUE_STYLES.length];
        const isLast = index === ABOUT_CONTENT.values.length - 1;

        return (
            <motion.article
                key={value.key}
                variants={fadeUp}
                className={cn(
                    'group rounded-3xl bg-white border border-border p-6 shadow-soft hover:shadow-pop hover:-translate-y-1 transition-all',
                    'sm:col-span-1 lg:col-span-4',
                    isLast &&
                        'sm:col-span-2 sm:max-w-[420px] sm:mx-auto lg:col-span-4 lg:col-start-5 lg:max-w-none'
                )}
            >
                <span
                    className={`inline-flex size-13 items-center justify-center rounded-2xl transition-colors ${color}`}
                >
                    <Icon className="size-6" />
                </span>

                <h3 className="mt-5 text-xl font-display font-bold">
                    {value.title}
                </h3>

                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {value.desc}
                </p>
            </motion.article>
        );
    })}
</motion.div>
                </div>
            </section>

            <section className="relative overflow-hidden bg-gradient-fun text-white py-14 sm:py-16">
                <div className="noise-overlay" />
                <div className="absolute -top-20 left-10 w-72 h-72 rounded-full bg-[hsl(var(--kche-yellow))]/25 blur-3xl animate-blob" />

                <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-sm font-bold font-display text-[hsl(var(--kche-yellow))] uppercase tracking-wider">
                        Diversión para todas las generaciones
                    </p>
                    <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-bold">
                        Un lugar para jugar, compartir y crear recuerdos
                    </h2>
                    <p className="mt-4 text-white/90 text-base sm:text-lg max-w-2xl mx-auto">
                        Conoce nuestras atracciones y encuentra la mejor experiencia para disfrutar en familia.
                    </p>

                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Link
                            to={ROUTES.atracciones}
                            className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--kche-yellow))] text-[hsl(var(--kche-dark))] px-7 py-3 font-bold font-display shadow-pop hover:scale-[1.03] transition-transform"
                        >
                            Explorar atracciones
                            <ArrowRight className="size-5" />
                        </Link>

                        <Link
                            to={ROUTES.contacto}
                            className="inline-flex items-center gap-2 rounded-full bg-white/15 text-white px-7 py-3 font-bold font-display border border-white/30 backdrop-blur hover:bg-white hover:text-[hsl(var(--kche-pink))] transition-colors"
                        >
                            Ver contacto
                        </Link>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}